// Timetable CRUD operations using direct fetch (bypassing Supabase client)

/// <reference lib="dom" />

import { handleApiCall } from './errors'
import { getAccessToken } from './auth-token'
import type {
  TimetableRecordWithRelations,
  CreateTimetableRecordRequest,
  UpdateTimetableRecordRequest,
  BulkCreateTimetableRecordsRequest,
  BulkCreateTimetableRecordsResponse,
  TimetableFilters,
} from '@timetable/types'

// Get environment variables
const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}


// Get all timetable records with filters
export async function getTimetableRecordsDirect(
  filters: TimetableFilters = {}
): Promise<TimetableRecordWithRelations[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*,teacher:teachers(*),subject:subjects(*),class_section:class_sections(*,class:classes(*)),period:schedule_periods(*),room:rooms(*)')

    if (filters.school_id) {
      params.append('school_id', `eq.${filters.school_id}`)
    }
    if (filters.teacher_id) {
      params.append('teacher_id', `eq.${filters.teacher_id}`)
    }
    if (filters.subject_id) {
      params.append('subject_id', `eq.${filters.subject_id}`)
    }
    if (filters.class_section_id) {
      params.append('class_section_id', `eq.${filters.class_section_id}`)
    }
    if (filters.period_id) {
      params.append('period_id', `eq.${filters.period_id}`)
    }
    if (filters.academic_year) {
      params.append('academic_year', `eq.${filters.academic_year}`)
    }
    if (filters.academic_year_id) {
      params.append('academic_year_id', `eq.${filters.academic_year_id}`)
    }
    if (filters.date) {
      params.append('valid_from', `lte.${filters.date}`)
      params.append('or', `(valid_to.is.null,valid_to.gte.${filters.date})`)
    }
    if (filters.active_only) {
      params.append('valid_to', 'is.null')
    }
    if (filters.day_of_week) {
      params.append('day_of_week', `eq.${filters.day_of_week}`)
    }
    params.append('order', 'created_at.asc')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/timetable_records?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch timetable records')
    }

    return await response.json()
  })
}

// Get timetable records by class section
export async function getByClassDirect(
  classSectionId: string,
  academicYear?: string
): Promise<TimetableRecordWithRelations[]> {
  return getTimetableRecordsDirect({
    class_section_id: classSectionId,
    academic_year: academicYear,
    active_only: true,
  })
}

// Get timetable records by teacher
export async function getByTeacherDirect(
  teacherId: string,
  academicYear?: string
): Promise<TimetableRecordWithRelations[]> {
  return getTimetableRecordsDirect({
    teacher_id: teacherId,
    academic_year: academicYear,
    active_only: true,
  })
}

// Get timetable records by subject
export async function getBySubjectDirect(
  subjectId: string,
  academicYear?: string
): Promise<TimetableRecordWithRelations[]> {
  return getTimetableRecordsDirect({
    subject_id: subjectId,
    academic_year: academicYear,
    active_only: true,
  })
}

// Get a single timetable record by ID
export async function getTimetableRecordByIdDirect(id: string): Promise<TimetableRecordWithRelations> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*,teacher:teachers(*),subject:subjects(*),class_section:class_sections(*,class:classes(*)),period:schedule_periods(*),room:rooms(*)')
    params.append('id', `eq.${id}`)

    const response = await fetch(`${SUPABASE_URL}/rest/v1/timetable_records?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/vnd.pgrst.object+json',
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Timetable record not found')
    }

    return await response.json()
  })
}

// Create a new timetable record
export async function createTimetableRecordDirect(
  record: CreateTimetableRecordRequest
): Promise<TimetableRecordWithRelations> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Check archived year immutability (Req 8.1, 8.2) — DB trigger is primary enforcement
    if (record.academic_year_id) {
      const yearRes = await fetch(
        `${SUPABASE_URL}/rest/v1/academic_years?id=eq.${record.academic_year_id}&select=status`,
        {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
            'Accept': 'application/vnd.pgrst.object+json',
          },
        }
      )
      if (yearRes.ok) {
        const yearData = await yearRes.json()
        if (yearData?.status === 'archived') {
          throw new Error('Cannot create timetable records for an archived academic year. Archived years are read-only.')
        }
      }
    }

    // Validate that the period is not a break period
    const period = await fetch(`${SUPABASE_URL}/rest/v1/schedule_periods?id=eq.${record.period_id}&select=is_break`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/vnd.pgrst.object+json',
      },
    })

    if (period.ok) {
      const periodData = await period.json()
      if (periodData.is_break) {
        throw new Error('Cannot assign classes to break periods. Break periods are reserved for non-instructional time.')
      }
    }

    const response = await fetch(`${SUPABASE_URL}/rest/v1/timetable_records`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(record),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to create timetable record')
    }

    const data: any[] = await response.json()
    const created = data[0]

    // Fetch with relations
    return getTimetableRecordByIdDirect(created.id)
  })
}

// Update a timetable record
export async function updateTimetableRecordDirect(
  id: string,
  updates: UpdateTimetableRecordRequest
): Promise<TimetableRecordWithRelations> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Check archived year immutability (Req 8.1, 8.2) — DB trigger is primary enforcement
    const existingRes = await fetch(
      `${SUPABASE_URL}/rest/v1/timetable_records?id=eq.${id}&select=academic_year_id`,
      {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/vnd.pgrst.object+json',
        },
      }
    )
    if (existingRes.ok) {
      const existing = await existingRes.json()
      const yearId = existing?.academic_year_id
      if (yearId) {
        const yearRes = await fetch(
          `${SUPABASE_URL}/rest/v1/academic_years?id=eq.${yearId}&select=status`,
          {
            headers: {
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${accessToken}`,
              'Accept': 'application/vnd.pgrst.object+json',
            },
          }
        )
        if (yearRes.ok) {
          const yearData = await yearRes.json()
          if (yearData?.status === 'archived') {
            throw new Error('Cannot modify timetable records for an archived academic year. Archived years are read-only.')
          }
        }
      }
    }

    const response = await fetch(`${SUPABASE_URL}/rest/v1/timetable_records?id=eq.${id}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(updates),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to update timetable record')
    }

    const data: any[] = await response.json()
    if (!data[0]) throw new Error('Timetable record not found')

    // Fetch with relations
    return getTimetableRecordByIdDirect(id)
  })
}

// Delete a timetable record
export async function deleteTimetableRecordDirect(id: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/timetable_records?id=eq.${id}`, {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to delete timetable record')
    }
  })
}

// Bulk create timetable records with transaction-like behavior
export async function bulkCreateTimetableRecordsDirect(
  request: BulkCreateTimetableRecordsRequest
): Promise<BulkCreateTimetableRecordsResponse> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/timetable_records`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(request.records),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(`Bulk operation failed: ${error.message}`)
    }

    const data: any[] = await response.json()

    // Fetch all created records with relations
    const created: TimetableRecordWithRelations[] = []
    for (const record of data) {
      const fullRecord = await getTimetableRecordByIdDirect(record.id)
      created.push(fullRecord)
    }

    return {
      created,
      errors: [],
    }
  })
}

// Bulk create with conflict detection (non-atomic, for selective copying)
export async function bulkCreateWithConflictDetectionDirect(
  request: BulkCreateTimetableRecordsRequest
): Promise<BulkCreateTimetableRecordsResponse> {
  return handleApiCall(async () => {
    const created: TimetableRecordWithRelations[] = []
    const errors: Array<{ record: CreateTimetableRecordRequest; error: string }> = []

    // Try inserting one by one to identify conflicts
    for (const record of request.records) {
      try {
        const result = await createTimetableRecordDirect(record)
        created.push(result)
      } catch (err: any) {
        errors.push({
          record,
          error: err.message || 'Unknown error',
        })
      }
    }

    return { created, errors }
  })
}

// Delete all records for a class section
export async function deleteAllForClassSectionDirect(classSectionId: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/timetable_records?class_section_id=eq.${classSectionId}`, {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to delete records')
    }
  })
}

// Delete all records for a specific week
export async function deleteWeekDirect(
  classSectionId: string,
  schoolId: string
): Promise<void> {
  return handleApiCall(async () => {
    const records = await getTimetableRecordsDirect({
      school_id: schoolId,
      class_section_id: classSectionId,
    })

    const recordIds = records.map((r) => r.id)

    if (recordIds.length > 0) {
      const accessToken = getAccessToken()
      if (!accessToken) throw new Error('Not authenticated')

      const response = await fetch(`${SUPABASE_URL}/rest/v1/timetable_records?id=in.(${recordIds.join(',')})`, {
        method: 'DELETE',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to delete week')
      }
    }
  })
}

// ============================================================================
// TEMPORAL VERSIONING API FUNCTIONS
// ============================================================================

// Get timetable records active on a specific date
export async function getByDateDirect(
  classSectionId: string,
  date: string,
  academicYear: string,
  academicYearId?: string
): Promise<TimetableRecordWithRelations[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*,teacher:teachers(*),subject:subjects(*),class_section:class_sections(*,class:classes(*)),period:schedule_periods(*),room:rooms(*)')
    params.append('class_section_id', `eq.${classSectionId}`)
    if (academicYearId) {
      params.append('academic_year_id', `eq.${academicYearId}`)
    } else {
      params.append('academic_year', `eq.${academicYear}`)
    }
    params.append('valid_from', `lte.${date}`)
    params.append('or', `(valid_to.is.null,valid_to.gte.${date})`)
    params.append('order', 'day_of_week.asc,period_id.asc')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/timetable_records?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch timetable records')
    }

    return await response.json()
  })
}

// Get currently active timetable records (valid_to IS NULL)
export async function getActiveByClassDirect(
  classSectionId: string,
  academicYear: string,
  academicYearId?: string
): Promise<TimetableRecordWithRelations[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*,teacher:teachers(*),subject:subjects(*),class_section:class_sections(*,class:classes(*)),period:schedule_periods(*),room:rooms(*)')
    params.append('class_section_id', `eq.${classSectionId}`)
    if (academicYearId) {
      params.append('academic_year_id', `eq.${academicYearId}`)
    } else {
      params.append('academic_year', `eq.${academicYear}`)
    }
    params.append('valid_to', 'is.null')
    params.append('order', 'day_of_week.asc,period_id.asc')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/timetable_records?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch active timetable records')
    }

    return await response.json()
  })
}

// Get complete change history for a specific period slot
export async function getHistoryDirect(
  classSectionId: string,
  dayOfWeek: 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY',
  periodId: string,
  academicYear: string,
  academicYearId?: string
): Promise<TimetableRecordWithRelations[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*,teacher:teachers(*),subject:subjects(*),class_section:class_sections(*,class:classes(*)),period:schedule_periods(*),room:rooms(*)')
    params.append('class_section_id', `eq.${classSectionId}`)
    params.append('day_of_week', `eq.${dayOfWeek}`)
    params.append('period_id', `eq.${periodId}`)
    if (academicYearId) {
      params.append('academic_year_id', `eq.${academicYearId}`)
    } else {
      params.append('academic_year', `eq.${academicYear}`)
    }
    params.append('order', 'valid_from.asc')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/timetable_records?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch history')
    }

    return await response.json()
  })
}

// Get timetable with assignments overlay for a specific date
export async function getWithAssignmentsDirect(
  classSectionId: string,
  date: string,
  academicYear: string
): Promise<TimetableRecordWithRelations[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Get base timetable for the date
    const baseTimetable = await getByDateDirect(classSectionId, date, academicYear)

    // Get assignments for the date
    const assignParams = new URLSearchParams()
    assignParams.append('select', '*,substitute_teacher:teachers!substitute_teacher_id(*)')
    assignParams.append('class_section_id', `eq.${classSectionId}`)
    assignParams.append('date', `eq.${date}`)

    const assignResponse = await fetch(`${SUPABASE_URL}/rest/v1/assignments?${assignParams}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    let assignments: any[] = []
    if (assignResponse.ok) {
      assignments = await assignResponse.json()
    }

    // Overlay assignments on base timetable
    return baseTimetable.map((record) => {
      const assignment = assignments?.find(
        (a: any) => a.period_id === record.period_id
      )

      if (assignment && assignment.substitute_teacher) {
        return {
          ...record,
          teacher: assignment.substitute_teacher,
          is_substitution: true,
          assignment_id: assignment.id,
          original_teacher_id: record.teacher_id,
        } as TimetableRecordWithRelations & {
          is_substitution: boolean
          assignment_id: string
          original_teacher_id: string
        }
      }

      return record
    })
  })
}

// ============================================================================
// STRUCTURAL CHANGE API
// ============================================================================

// Create a structural change to the base timetable
export async function createStructuralChangeDirect(
  request: import('@timetable/types').CreateStructuralChangeRequest
): Promise<string> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Validate effective date is not in the past
    const effectiveDate = new Date(request.effective_date + 'T00:00:00')
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    if (effectiveDate <= today) {
      throw new Error('Effective date must be in the future')
    }

    // Call PostgreSQL RPC function
    const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/create_structural_change`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        p_old_record_id: request.old_record_id,
        p_new_teacher_id: request.new_teacher_id,
        p_new_subject_id: request.new_subject_id,
        p_effective_date: request.effective_date,
        p_created_by: request.created_by || null,
      }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to create structural change')
    }

    return await response.json()
  })
}

// Get a preview of what will change when creating a structural change
export async function previewStructuralChangeDirect(
  oldRecordId: string,
  newTeacherId: string,
  newSubjectId: string,
  effectiveDate: string
): Promise<{
  oldRecord: TimetableRecordWithRelations
  newRecordPreview: Partial<TimetableRecordWithRelations>
}> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Get the old record
    const oldRecord = await getTimetableRecordByIdDirect(oldRecordId)

    // Calculate the day before effective date for valid_to
    const effectiveDateObj = new Date(effectiveDate)
    const dayBefore = new Date(effectiveDateObj)
    dayBefore.setDate(dayBefore.getDate() - 1)
    const dayBeforeStr = dayBefore.toISOString().split('T')[0]

    // Get the new teacher
    const teacherParams = new URLSearchParams()
    teacherParams.append('select', '*')
    teacherParams.append('id', `eq.${newTeacherId}`)

    const teacherResponse = await fetch(`${SUPABASE_URL}/rest/v1/teachers?${teacherParams}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/vnd.pgrst.object+json',
      },
    })

    if (!teacherResponse.ok) {
      throw new Error('Failed to fetch new teacher')
    }

    const newTeacher = await teacherResponse.json()

    // Get the new subject
    const subjectParams = new URLSearchParams()
    subjectParams.append('select', '*')
    subjectParams.append('id', `eq.${newSubjectId}`)

    const subjectResponse = await fetch(`${SUPABASE_URL}/rest/v1/subjects?${subjectParams}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/vnd.pgrst.object+json',
      },
    })

    if (!subjectResponse.ok) {
      throw new Error('Failed to fetch new subject')
    }

    const newSubject = await subjectResponse.json()

    // Create preview of new record
    const newRecordPreview: Partial<TimetableRecordWithRelations> = {
      school_id: oldRecord.school_id,
      academic_year: oldRecord.academic_year,
      valid_from: effectiveDate,
      valid_to: oldRecord.valid_to,
      day_of_week: oldRecord.day_of_week,
      period_id: oldRecord.period_id,
      teacher_id: newTeacherId,
      subject_id: newSubjectId,
      class_section_id: oldRecord.class_section_id,
      teacher: newTeacher,
      subject: newSubject,
      class_section: oldRecord.class_section,
      period: oldRecord.period,
    }

    return {
      oldRecord: {
        ...oldRecord,
        valid_to: dayBeforeStr,
      },
      newRecordPreview,
    }
  })
}
